import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import type { DashboardResponse, Article, CrawlJobResponse } from '@/types/api';

type SortField = 'createdAt' | 'publishedAt' | 'actualRating' | 'visitorUpvote' | 'visitorDownvote' | 'title';
type SortOrder = 'asc' | 'desc';

interface ArticleFilters {
  sortBy: SortField;
  sortOrder: SortOrder;
  minRating?: number;
  maxRating?: number;
  minUpvote?: number;
  minDownvote?: number;
  dateFrom?: string;
  dateTo?: string;
}

export function WebsiteDetail() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'articles' | 'settings'>('overview');

  // Articles state
  const [articles, setArticles] = useState<Article[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesLoadingMore, setArticlesLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState<ArticleFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [showFilters, setShowFilters] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const [verifying, setVerifying] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<'dns' | 'meta' | 'file'>('meta');

  // Crawl job state
  const [crawlJob, setCrawlJob] = useState<CrawlJobResponse | null>(null);
  const [showCrawlConfirm, setShowCrawlConfirm] = useState(false);
  const [pendingActiveJob, setPendingActiveJob] = useState<CrawlJobResponse | null>(null);
  const crawlPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedSitemapUrl, setEditedSitemapUrl] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [deletingWebsite, setDeletingWebsite] = useState(false);

  // Sitemap check state
  const [sitemapStatus, setSitemapStatus] = useState<{
    checking: boolean;
    hasChanges: boolean;
    newUrls: string[];
    updatedUrls: { url: string; lastmod: string | null }[];
    totalInSitemap: number;
    totalExisting: number;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (!websiteId) return;
      try {
        const response = await api.getDashboard(websiteId);
        setData(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [websiteId]);

  // Fetch articles with filters
  const fetchArticles = useCallback(async (reset = false) => {
    if (!websiteId || activeTab !== 'articles') return;

    if (reset) {
      setArticlesLoading(true);
      setArticles([]);
      setNextCursor(null);
    } else {
      setArticlesLoadingMore(true);
    }

    try {
      const response = await api.listArticles(websiteId, {
        limit: 20,
        startAfter: reset ? undefined : (nextCursor ?? undefined),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        minRating: filters.minRating,
        maxRating: filters.maxRating,
        minUpvote: filters.minUpvote,
        minDownvote: filters.minDownvote,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });

      if (reset) {
        setArticles(response.articles);
      } else {
        setArticles((prev) => [...prev, ...response.articles]);
      }
      setNextCursor(response.nextCursor);
      setHasMore(response.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setArticlesLoading(false);
      setArticlesLoadingMore(false);
    }
  }, [websiteId, activeTab, nextCursor, filters]);

  // Initial fetch when switching to articles tab
  useEffect(() => {
    if (activeTab === 'articles' && articles.length === 0 && !articlesLoading) {
      fetchArticles(true);
    }
  }, [activeTab, articles.length, articlesLoading, fetchArticles]);

  // Reset and refetch when filters change
  useEffect(() => {
    if (activeTab === 'articles') {
      fetchArticles(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, websiteId]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !articlesLoadingMore && !articlesLoading) {
          fetchArticles(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, articlesLoadingMore, articlesLoading, fetchArticles]);

  const handleToggleEnabled = async (articleId: string, currentEnabled: boolean) => {
    if (!websiteId) return;
    try {
      await api.updateArticle(websiteId, articleId, { enabled: !currentEnabled });
      setArticles(
        articles.map((a) =>
          a.id === articleId ? { ...a, enabled: !currentEnabled } : a
        )
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update article');
    }
  };

  const handleSort = (field: SortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleFilterChange = (key: keyof ArticleFilters, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  };

  const handleVerify = async () => {
    if (!websiteId) return;
    setVerifying(true);
    try {
      await api.verifyDomain(websiteId, verificationMethod);
      // Refresh data
      const response = await api.getDashboard(websiteId);
      setData(response);
      alert('Domain verified successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Poll crawl job status
  const pollCrawlStatus = useCallback(async (jobId: string) => {
    try {
      const job = await api.getCrawlJobStatus(jobId);
      setCrawlJob(job);

      if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        // Stop polling
        if (crawlPollingRef.current) {
          clearInterval(crawlPollingRef.current);
          crawlPollingRef.current = null;
        }
        // Refresh dashboard data
        if (websiteId) {
          const response = await api.getDashboard(websiteId);
          setData(response);
        }
      }
    } catch (err) {
      console.error('Failed to poll crawl status:', err);
    }
  }, [websiteId]);

  // Start polling when crawl job is active
  useEffect(() => {
    if (crawlJob && (crawlJob.status === 'pending' || crawlJob.status === 'running')) {
      // Start polling every 2 seconds
      crawlPollingRef.current = setInterval(() => {
        pollCrawlStatus(crawlJob.id);
      }, 2000);
    }

    return () => {
      if (crawlPollingRef.current) {
        clearInterval(crawlPollingRef.current);
        crawlPollingRef.current = null;
      }
    };
  }, [crawlJob?.id, crawlJob?.status, pollCrawlStatus]);

  // Check for active crawl job on mount
  useEffect(() => {
    async function checkActiveCrawlJob() {
      if (!websiteId) return;
      try {
        const { activeJob } = await api.getActiveCrawlJob(websiteId);
        if (activeJob) {
          setCrawlJob(activeJob);
        }
      } catch (err) {
        console.error('Failed to check active crawl job:', err);
      }
    }
    checkActiveCrawlJob();
  }, [websiteId]);

  // Check sitemap for changes on page load
  useEffect(() => {
    async function checkSitemapChanges() {
      if (!websiteId || !data?.website.sitemapUrl || data?.website.verificationStatus !== 'verified') {
        return;
      }

      setSitemapStatus({
        checking: true,
        hasChanges: false,
        newUrls: [],
        updatedUrls: [],
        totalInSitemap: 0,
        totalExisting: 0,
      });

      try {
        const result = await api.checkSitemap(websiteId);
        setSitemapStatus({
          checking: false,
          hasChanges: result.hasChanges,
          newUrls: result.newUrls,
          updatedUrls: result.updatedUrls,
          totalInSitemap: result.totalInSitemap,
          totalExisting: result.totalExisting,
        });
      } catch (err) {
        console.error('Failed to check sitemap:', err);
        setSitemapStatus(null);
      }
    }

    checkSitemapChanges();
  }, [websiteId, data?.website.sitemapUrl, data?.website.verificationStatus]);

  // Handle sync new/updated URLs
  const handleSyncChanges = async () => {
    if (!websiteId || !sitemapStatus) return;

    const urlsToSync = [
      ...sitemapStatus.newUrls,
      ...sitemapStatus.updatedUrls.map(u => u.url),
    ];

    if (urlsToSync.length === 0) return;

    setSyncing(true);
    try {
      const result = await api.crawlAndAnalyze(websiteId, urlsToSync);
      alert(`Sync complete! Created: ${result.results.created}, Updated: ${result.results.updated}, Analyzed: ${result.results.analyzed}`);

      // Refresh dashboard and sitemap status
      const [dashboardResponse] = await Promise.all([
        api.getDashboard(websiteId),
        api.checkSitemap(websiteId).then(r => {
          setSitemapStatus({
            checking: false,
            hasChanges: r.hasChanges,
            newUrls: r.newUrls,
            updatedUrls: r.updatedUrls,
            totalInSitemap: r.totalInSitemap,
            totalExisting: r.totalExisting,
          });
        }),
      ]);
      setData(dashboardResponse);

      // Refresh articles if on articles tab
      if (activeTab === 'articles') {
        fetchArticles(true);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to sync changes');
    } finally {
      setSyncing(false);
    }
  };

  const handleStartCrawl = async (force = false) => {
    if (!websiteId) return;

    try {
      const result = await api.startCrawl(websiteId, force);

      if ('error' in result) {
        // Active job exists
        setPendingActiveJob(result.activeJob);
        setShowCrawlConfirm(true);
        return;
      }

      // Success - job created
      setCrawlJob(result.job);
      setShowCrawlConfirm(false);
      setPendingActiveJob(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start crawl');
    }
  };

  const handleCancelCrawl = async () => {
    if (!crawlJob) return;

    try {
      await api.cancelCrawlJob(crawlJob.id);
      setCrawlJob((prev) => prev ? { ...prev, status: 'cancelled' } : null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel crawl');
    }
  };

  const handleConfirmForceRestart = async () => {
    setShowCrawlConfirm(false);
    setPendingActiveJob(null);
    await handleStartCrawl(true);
  };

  const handleCancelConfirm = () => {
    setShowCrawlConfirm(false);
    setPendingActiveJob(null);
  };

  const handleRegenerateKey = async () => {
    if (!websiteId) return;
    if (!confirm('Are you sure you want to regenerate the API key? This will invalidate the current key and you will need to update your badge integration.')) {
      return;
    }
    setRegeneratingKey(true);
    try {
      const result = await api.regenerateBadgeKey(websiteId);
      // Update local data with new key
      if (data) {
        setData({
          ...data,
          website: {
            ...data.website,
            badgeApiKey: result.badgeApiKey,
          },
        });
      }
      alert('API key regenerated successfully! Please update your badge integration with the new key.');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to regenerate API key');
    } finally {
      setRegeneratingKey(false);
    }
  };

  const handleEditSettings = () => {
    if (data) {
      setEditedName(data.website.domain); // Using domain as name for now
      setEditedSitemapUrl(data.website.sitemapUrl || '');
      setIsEditingSettings(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSettings(false);
    setEditedName('');
    setEditedSitemapUrl('');
  };

  const handleSaveSettings = async () => {
    if (!websiteId) return;
    setSavingSettings(true);
    try {
      await api.updateWebsite(websiteId, {
        name: editedName,
        sitemapUrl: editedSitemapUrl || undefined,
      });
      // Refresh data
      const response = await api.getDashboard(websiteId);
      setData(response);
      setIsEditingSettings(false);
      alert('Website settings updated successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteWebsite = async () => {
    if (!websiteId) return;
    if (!confirm('Are you sure you want to delete this website? This action cannot be undone. All articles and data associated with this website will be permanently deleted.')) {
      return;
    }
    setDeletingWebsite(true);
    try {
      await api.deleteWebsite(websiteId);
      alert('Website deleted successfully');
      navigate('/websites');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete website');
      setDeletingWebsite(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-8 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error || 'Failed to load website'}</p>
      </div>
    );
  }

  const { website, statistics, recentArticles, scoreDistribution } = data;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{website.domain}</h1>
          <span
            className={`inline-flex mt-1 px-2 py-1 text-xs font-medium rounded-full ${
              website.verificationStatus === 'verified'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {website.verificationStatus}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {(['overview', 'articles', 'settings'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Sitemap Changes Notification */}
          {sitemapStatus?.hasChanges && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-blue-800 font-medium">
                  Sitemap changes detected
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {sitemapStatus.newUrls.length > 0 && `${sitemapStatus.newUrls.length} new articles`}
                  {sitemapStatus.newUrls.length > 0 && sitemapStatus.updatedUrls.length > 0 && ', '}
                  {sitemapStatus.updatedUrls.length > 0 && `${sitemapStatus.updatedUrls.length} updated articles`}
                </p>
              </div>
              <button
                onClick={handleSyncChanges}
                disabled={syncing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          )}

          {sitemapStatus?.checking && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
              <svg className="w-5 h-5 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Checking sitemap for changes...</p>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">Total Articles</p>
              <p className="text-3xl font-bold text-gray-900">{statistics.totalArticles}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">Analyzed</p>
              <p className="text-3xl font-bold text-gray-900">{statistics.analyzedArticles}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">Avg. Rating</p>
              <p className="text-3xl font-bold text-gray-900">{statistics.avgRating}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">Total Votes</p>
              <p className="text-3xl font-bold text-gray-900">{statistics.totalVotes}</p>
            </div>
          </div>

          {/* Score Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Score Distribution</h3>
            <div className="flex gap-4">
              {Object.entries(scoreDistribution).map(([label, count]) => (
                <div key={label} className="flex-1 text-center">
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-sm text-gray-500 capitalize">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Articles */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Recent Articles</h3>
            </div>
            {recentArticles.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No articles yet</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/websites/${websiteId}/articles/${article.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{article.title}</p>
                      <p className="text-sm text-gray-500 truncate">{article.url}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {article.actualRating != null && (
                        <span
                          className={`px-2 py-1 text-sm font-medium rounded ${
                            article.actualRating >= 70
                              ? 'bg-green-100 text-green-800'
                              : article.actualRating >= 50
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {article.actualRating}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'articles' && (
        <div className="space-y-4">
          {/* Filter Toggle & Clear */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {(filters.minRating !== undefined || filters.maxRating !== undefined || filters.minUpvote !== undefined || filters.minDownvote !== undefined || filters.dateFrom || filters.dateTo) && (
                <span className="w-2 h-2 bg-blue-600 rounded-full" />
              )}
            </button>
            {(filters.minRating !== undefined || filters.maxRating !== undefined || filters.minUpvote !== undefined || filters.minDownvote !== undefined || filters.dateFrom || filters.dateTo) && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Date Range */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
                  <input
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
                  <input
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Rating Range */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.minRating ?? ''}
                    onChange={(e) => handleFilterChange('minRating', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.maxRating ?? ''}
                    onChange={(e) => handleFilterChange('maxRating', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="100"
                  />
                </div>

                {/* Vote Filters */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Upvotes</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.minUpvote ?? ''}
                    onChange={(e) => handleFilterChange('minUpvote', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Downvotes</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.minDownvote ?? ''}
                    onChange={(e) => handleFilterChange('minDownvote', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Articles Table */}
          {articlesLoading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-4 border-b border-gray-100">
                  <div className="h-5 w-64 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-500">
                {filters.minRating !== undefined || filters.maxRating !== undefined || filters.minUpvote !== undefined || filters.minDownvote !== undefined || filters.dateFrom || filters.dateTo
                  ? 'Try adjusting your filters'
                  : 'Articles will appear here once they are crawled'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('title')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Article
                        {filters.sortBy === 'title' && (
                          <svg className={`w-4 h-4 ${filters.sortOrder === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('actualRating')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Score
                        {filters.sortBy === 'actualRating' && (
                          <svg className={`w-4 h-4 ${filters.sortOrder === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('visitorUpvote')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Upvotes
                        {filters.sortBy === 'visitorUpvote' && (
                          <svg className={`w-4 h-4 ${filters.sortOrder === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('visitorDownvote')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Downvotes
                        {filters.sortBy === 'visitorDownvote' && (
                          <svg className={`w-4 h-4 ${filters.sortOrder === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('publishedAt')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Published
                        {filters.sortBy === 'publishedAt' && (
                          <svg className={`w-4 h-4 ${filters.sortOrder === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Crawled
                        {filters.sortBy === 'createdAt' && (
                          <svg className={`w-4 h-4 ${filters.sortOrder === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enabled
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {articles.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <Link
                          to={`/websites/${websiteId}/articles/${article.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {article.title}
                        </Link>
                        <p className="text-sm text-gray-500 truncate max-w-md">{article.url}</p>
                      </td>
                      <td className="px-6 py-4">
                        {article.actualRating != null ? (
                          <span
                            className={`inline-flex px-2 py-1 text-sm font-medium rounded ${
                              article.actualRating >= 70
                                ? 'bg-green-100 text-green-800'
                                : article.actualRating >= 50
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {article.actualRating}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-green-600 font-medium">
                        {article.visitorUpvote}
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600 font-medium">
                        {article.visitorDownvote}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {article.createdAt ? new Date(article.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleEnabled(article.id, article.enabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            article.enabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              article.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Infinite Scroll Trigger */}
              <div ref={loadMoreRef} className="p-4">
                {articlesLoadingMore && (
                  <div className="flex items-center justify-center gap-2 text-gray-500">
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading more...
                  </div>
                )}
                {!hasMore && articles.length > 0 && (
                  <p className="text-center text-sm text-gray-500">No more articles</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Website Settings Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Website Settings</h3>
              {!isEditingSettings && (
                <button
                  onClick={handleEditSettings}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Settings
                </button>
              )}
            </div>

            {isEditingSettings ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domain (Read-only)</label>
                  <input
                    type="text"
                    value={data?.website.domain}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Domain cannot be changed after registration</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website Name</label>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="My Website"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sitemap URL</label>
                  <input
                    type="url"
                    value={editedSitemapUrl}
                    onChange={(e) => setEditedSitemapUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/sitemap.xml"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingSettings ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={savingSettings}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Domain</p>
                  <p className="text-gray-900 font-medium">{data?.website.domain}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Website Name</p>
                  <p className="text-gray-900 font-medium">{data?.website.domain}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sitemap URL</p>
                  <p className="text-gray-900 font-medium">{data?.website.sitemapUrl || 'Not configured'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Badge API Key Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Badge API Key</h3>
              <button
                onClick={handleRegenerateKey}
                disabled={regeneratingKey}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regeneratingKey ? 'Regenerating...' : 'Regenerate Key'}
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Use this API key to initialize the SheerAI badge on your website. Keep it secure.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg p-3 font-mono text-sm overflow-x-auto">
                {website.badgeApiKey}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(website.badgeApiKey);
                  alert('API key copied to clipboard!');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Badge Integration Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Badge Integration</h3>
            <p className="text-gray-600 mb-4">
              Add the following code to your website's HTML to display the SheerAI badge.
            </p>
            <div className="bg-gray-900 rounded-lg p-4 text-green-400 font-mono text-sm overflow-x-auto">
              <code>{`<script src="https://YOUR-PROJECT.web.app/badge/sheer-badge.js"></script>
<script>
  window.SheerBadge.init({
    apiKey: '${website.badgeApiKey}',
    position: 'bottom-right'
  });
</script>`}</code>
            </div>
            <button
              onClick={() => {
                const code = `<script src="https://YOUR-PROJECT.web.app/badge/sheer-badge.js"></script>
<script>
  window.SheerBadge.init({
    apiKey: '${website.badgeApiKey}',
    position: 'bottom-right'
  });
</script>`;
                navigator.clipboard.writeText(code);
                alert('Badge code copied to clipboard!');
              }}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Copy Code
            </button>
          </div>

          {/* Verification Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Domain Verification</h3>

            {website.verificationStatus === 'verified' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium">✓ Domain is verified</p>
                <p className="text-sm text-green-600 mt-1">
                  Verified using: {website.verificationMethod || 'unknown'} method
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600 mb-4">
                  Choose a verification method and follow the instructions below:
                </p>

                {/* Method selector */}
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => setVerificationMethod('meta')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      verificationMethod === 'meta'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    HTML Meta Tag (Easiest)
                  </button>
                  <button
                    onClick={() => setVerificationMethod('dns')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      verificationMethod === 'dns'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    DNS TXT Record
                  </button>
                </div>

                {/* Instructions */}
                {verificationMethod === 'meta' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-medium text-blue-900 mb-2">HTML Meta Tag Verification</p>
                    <p className="text-sm text-blue-700 mb-3">
                      Add this meta tag to the &lt;head&gt; section of your website's homepage:
                    </p>
                    <div className="bg-white rounded border border-blue-200 p-3 font-mono text-sm overflow-x-auto">
                      <code className="text-blue-900">
                        {`<meta name="sheerai-verification" content="${website.verificationToken}">`}
                      </code>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Works with most platforms including Tistory, WordPress, Wix, etc.
                    </p>
                  </div>
                )}

                {verificationMethod === 'dns' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-medium text-blue-900 mb-2">DNS TXT Record Verification</p>
                    <p className="text-sm text-blue-700 mb-3">
                      Add a DNS TXT record to your domain:
                    </p>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-medium text-blue-900">Host/Name:</span>
                        <div className="bg-white rounded border border-blue-200 p-2 font-mono text-sm">
                          _sheerai.{website.domain}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-blue-900">Value:</span>
                        <div className="bg-white rounded border border-blue-200 p-2 font-mono text-sm overflow-x-auto">
                          sheerai-verification={website.verificationToken}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleVerify}
                  disabled={verifying}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {verifying ? 'Verifying...' : 'Verify Domain'}
                </button>
              </div>
            )}
          </div>

          {/* Crawl Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Manual Crawl</h3>
            <p className="text-gray-600 mb-4">
              Manually trigger a crawl to discover articles from your sitemap. This will delete all existing articles and re-crawl from scratch.
            </p>

            {/* Crawl Progress */}
            {crawlJob && (crawlJob.status === 'pending' || crawlJob.status === 'running') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-blue-800 font-medium">
                    {crawlJob.status === 'pending' ? 'Preparing crawl...' : 'Crawling in progress...'}
                  </p>
                  <button
                    onClick={handleCancelCrawl}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Cancel
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-blue-200 rounded-full h-3 mb-2">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${crawlJob.progress}%` }}
                  />
                </div>

                <div className="flex justify-between text-sm text-blue-700">
                  <span>{crawlJob.processedUrls} / {crawlJob.totalUrls} URLs processed</span>
                  <span>{crawlJob.progress}%</span>
                </div>

                {crawlJob.currentUrl && (
                  <p className="text-xs text-blue-600 mt-2 truncate">
                    Processing: {crawlJob.currentUrl}
                  </p>
                )}

                <div className="flex gap-4 mt-2 text-xs text-blue-700">
                  <span>Created: {crawlJob.createdUrls}</span>
                  <span>Failed: {crawlJob.failedUrls}</span>
                </div>
              </div>
            )}

            {/* Completed State */}
            {crawlJob && crawlJob.status === 'completed' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium">Crawl completed successfully!</p>
                <p className="text-sm text-green-700 mt-2">
                  Processed {crawlJob.totalUrls} URLs • Created {crawlJob.createdUrls} articles • Failed {crawlJob.failedUrls}
                </p>
              </div>
            )}

            {/* Failed State */}
            {crawlJob && crawlJob.status === 'failed' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">Crawl failed</p>
                <p className="text-sm text-red-700 mt-2">
                  {crawlJob.error || 'An unknown error occurred'}
                </p>
              </div>
            )}

            {/* Cancelled State */}
            {crawlJob && crawlJob.status === 'cancelled' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">Crawl was cancelled</p>
                <p className="text-sm text-yellow-700 mt-2">
                  Processed {crawlJob.processedUrls} of {crawlJob.totalUrls} URLs before cancellation
                </p>
              </div>
            )}

            <button
              onClick={() => handleStartCrawl(false)}
              disabled={
                (crawlJob && (crawlJob.status === 'pending' || crawlJob.status === 'running')) ||
                !website.sitemapUrl ||
                website.verificationStatus !== 'verified'
              }
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {crawlJob && (crawlJob.status === 'pending' || crawlJob.status === 'running')
                ? 'Crawling...'
                : 'Start Manual Crawl'}
            </button>

            {!website.sitemapUrl && (
              <p className="text-sm text-yellow-600 mt-2">
                ⚠ Sitemap URL not configured
              </p>
            )}
            {website.verificationStatus !== 'verified' && (
              <p className="text-sm text-yellow-600 mt-2">
                ⚠ Domain must be verified first
              </p>
            )}
          </div>

          {/* Crawl Confirmation Modal */}
          {showCrawlConfirm && pendingActiveJob && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Crawl Already in Progress</h3>
                <p className="text-gray-600 mb-4">
                  A crawl is already running for this website ({pendingActiveJob.progress}% complete).
                </p>
                <p className="text-gray-600 mb-6">
                  Do you want to cancel it and start a new crawl from scratch?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelConfirm}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Keep Current
                  </button>
                  <button
                    onClick={handleConfirmForceRestart}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    Restart Crawl
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Website Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-red-200">
            <h3 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h3>
            <p className="text-gray-600 mb-4">
              Once you delete this website, there is no going back. All articles, analytics data, and settings associated with this website will be permanently deleted.
            </p>
            <button
              onClick={handleDeleteWebsite}
              disabled={deletingWebsite}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {deletingWebsite ? 'Deleting...' : 'Delete Website'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
